import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth, getAccessToken, getRefreshToken } from '@/services/auth'
import type { User, UserRole } from '@/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => {
    const hasToken = !!getAccessToken()
    const hasUser = !!user.value
    return hasToken && hasUser
  })

  const userRole = computed(() => user.value?.role || null)
  const isAdmin = computed(() => userRole.value === 'ADMIN')
  const isModerator = computed(() => ['ADMIN', 'MODERATOR'].includes(userRole.value || ''))

  const login = async (credentials: { username: string; password: string }) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await auth.login(credentials)
      
      // Set tokens after successful login
      const { setTokens } = await import('@/services/auth')
      setTokens(response.accessToken, response.refreshToken, response.expiresIn)
      
      user.value = response.user
      
      return { success: true, user: response.user }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка авторизации'
      error.value = errorMessage
      return { 
        success: false, 
        error: errorMessage 
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      await auth.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      user.value = null
      isLoading.value = false
    }
  }

  const checkAuth = async () => {
    const accessToken = getAccessToken()
    const refreshTokenValue = getRefreshToken()
    
    if (!accessToken && !refreshTokenValue) {
      user.value = null
      return false
    }
    
    // If we have access token and user, we're good
    if (accessToken && user.value) {
      return true
    }
    
    // Try to get current user with existing token
    try {
      isLoading.value = true
      const currentUser = await auth.getCurrentUser()
      user.value = currentUser
      return true
    } catch (err) {
      // If we have a refresh token, try to refresh
      if (refreshTokenValue) {
        try {
          await auth.refreshAccessToken(refreshTokenValue)
          const currentUser = await auth.getCurrentUser()
          user.value = currentUser
          return true
        } catch (refreshErr) {
          // Refresh failed, continue to clear tokens
        }
      }
      
      // All attempts failed, clear everything
      user.value = null
      const { clearTokens } = await import('@/services/auth')
      clearTokens()
      return false
    } finally {
      isLoading.value = false
    }
  }

  const refreshToken = async () => {
    const refreshTokenValue = getRefreshToken()
    if (!refreshTokenValue) {
      throw new Error('No refresh token available')
    }
    
    try {
      await auth.refreshAccessToken(refreshTokenValue)
      return true
    } catch (err) {
      console.error('Token refresh failed:', err)
      user.value = null
      return false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    isLoading.value = true
    error.value = null
    
    try {
      await auth.changePassword(currentPassword, newPassword)
      return { success: true }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ошибка смены пароля'
      error.value = errorMessage
      return { 
        success: false, 
        error: errorMessage 
      }
    } finally {
      isLoading.value = false
    }
  }

  const hasPermission = (requiredRole: UserRole): boolean => {
    if (!user.value) return false
    
    const roleHierarchy: Record<UserRole, number> = {
      'EDITOR': 1,
      'MODERATOR': 2,
      'ADMIN': 3
    }
    
    return roleHierarchy[user.value.role] >= roleHierarchy[requiredRole]
  }

  // Auth initialization is handled by App.vue on mount

  return {
    user: computed(() => user.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    isAuthenticated,
    userRole,
    isAdmin,
    isModerator,
    refreshToken: computed(() => getRefreshToken()),
    login,
    logout,
    checkAuth,
    refreshAccessToken: refreshToken,
    changePassword,
    hasPermission
  }
})
